/**
 * 직독직해를 읽는 중에 그 문장의 구문을 곁들여 보여준다.
 *
 * **밑줄을 긋지 않는다. 색으로만 구분한다.** 지인에듀 종이처럼 밑줄을 층으로 쌓아
 * 봤더니, 관계절 안에 전치사구가 또 드는 문장에서 한 낱말 아래에 밑줄이 서너 겹씩
 * 깔려 정작 읽어야 할 글이 묻혔다. 끊어읽기 단위로 잘려 그려지다 보니 괄호도 반
 * 토막이 났다. 화면은 종이가 아니라서 폭이 좁고 줄이 자주 접힌다.
 *
 * 대신 이렇게 셋으로 알린다.
 *
 * - **낱말의 색**: 파랑이 주어, 빨강이 동사, 초록이 목적어, 하늘색이 보어.
 * - **글자 위**: 구·절의 이름(전치사구, 관계절 …). 괄호로 어디까지인지도 보인다.
 * - **글자 아래**: S, V, O. 아이는 색만으로는 무엇인지 모른다.
 *
 * **이름표는 낱말마다 세 칸(위·가운데·아래)을 잡아 그 자리에 넣는다.** 글줄 밖으로
 * 띄워(`position: absolute`) 봤더니 inline 요소에는 기준 높이가 없어 이름이 글자
 * 위로 내려앉아 겹쳤다. 칸을 미리 잡아 두면 이름이 있든 없든 낱말의 높이가 같아
 * 절대 겹치지 않는다.
 *
 * 얇고 작게 써서 연필로 적어 둔 것처럼 보이게 한다. 굵게 쓰면 정작 읽어야 할
 * 문장보다 먼저 눈에 들어온다.
 *
 * ⚠ `syntax_analysis/` 의 화면은 아이가 직접 태그를 붙이는 문제 풀이다.
 * **그것을 가져다 쓰거나 고치지 않는다**(그 폴더의 재구축 금지 주석 참고).
 *
 * 색은 해설지 PDF(`api/utils/pdf/common/palette.py` 의 `SYNTAX_*`)와 같은 값이다.
 * 종이와 화면이 다른 색이면 아이가 같은 것을 두 가지로 배운다.
 */

import { phraseInfo } from "../syntax_analysis/constants";

export const SUBJECT_COLOR = "#2C5FA8";
export const VERB_COLOR = "#C2453D";
export const OBJECT_COLOR = "#0E8C7F";
export const COMPLEMENT_COLOR = "#4A8FB5";
const PHRASE_COLOR = "#C98A00"; // 구
const CLAUSE_COLOR = "#A34A87"; // 절

// 낱말에 색을 입히고 아래에 이름을 적는 문장 성분.
const ROLE_STYLE = {
  s: { label: "S", color: SUBJECT_COLOR },
  fs: { label: "S", color: SUBJECT_COLOR },
  ls: { label: "S", color: SUBJECT_COLOR },
  v: { label: "V", color: VERB_COLOR },
  o: { label: "O", color: OBJECT_COLOR },
  io: { label: "I·O", color: OBJECT_COLOR },
  do: { label: "D·O", color: OBJECT_COLOR },
  fo: { label: "O", color: OBJECT_COLOR },
  sc: { label: "S·C", color: COMPLEMENT_COLOR },
  oc: { label: "O·C", color: COMPLEMENT_COLOR },
};

// 이름을 붙이지 않는 것. 접속사나 주석은 이 자리에서 알려 줄 것이 없다.
const SKIPPED_TYPE_SET = new Set(["conj", "modificant", "ann:box", "ann:text", "ccls"]);

const LETTER_PATTERN = /[A-Za-z0-9']/;

const lettersOf = (text) =>
  [...(text || "")]
    .filter((one) => LETTER_PATTERN.test(one))
    .join("")
    .toLowerCase();

/**
 * 끊어읽기 덩어리가 구문분석의 어느 토큰들인지 찾는다.
 *
 * 둘은 같은 문장을 다르게 쪼갠 것이라 이어 주는 열쇠가 없다. **글자만 남겨 놓고
 * 앞에서부터 맞춰 센다.** 문장부호와 띄어쓰기가 양쪽에서 다르게 들어가 있어
 * (`ca` + `n't` 처럼 토큰이 낱말 가운데를 자르기도 한다) 낱말 단위로는 어긋난다.
 *
 * 운영 데이터 394문장으로 전부 맞는 것을 확인했다. 그래도 어긋나면 null 을 주고
 * 화면은 그 자리를 그냥 비운다. 못 맞춘 것을 억지로 그리면 엉뚱한 곳에 색이 붙는다.
 */
export const findChunkTokenRange = (tokenList, englishSentence, chunkIndex) => {
  if (!tokenList?.length) return null;

  let tokenText = "";
  const ownerList = [];
  tokenList.forEach((token) => {
    [...(token.text || "")].forEach((one) => {
      if (!LETTER_PATTERN.test(one)) return;
      tokenText += one.toLowerCase();
      ownerList.push(token.index);
    });
  });

  const chunkList = String(englishSentence || "")
    .split("/")
    .map((one) => one.trim())
    .filter(Boolean);

  let cursor = 0;
  for (let index = 0; index < chunkList.length; index++) {
    const need = lettersOf(chunkList[index]);
    if (tokenText.slice(cursor, cursor + need.length) !== need) return null;

    if (index === chunkIndex) {
      return { from: ownerList[cursor], to: ownerList[cursor + need.length - 1] };
    }
    cursor += need.length;
  }
  return null;
};

/** 절은 대괄호, 구는 소괄호. 지인에듀와 같은 규칙이다. */
const bracketOf = (type) => (type.startsWith("cls:") ? ["[", "]"] : ["(", ")"]);

/**
 * `fallback` 은 **그릴 것이 없을 때 대신 보여줄 원문**이다. 이것을 받지 않으면
 * 화면이 그 자리를 통째로 비운다. 부모가 `<SyntaxHint />` 를 만드는 순간 그 값은
 * 이미 참이라, 여기서 null 을 내도 부모의 `||` 가 원문으로 넘어가지 못한다.
 * 표시할 구문이 없는 덩어리(전치사구 하나 없는 명사구 등)에서 실제로 글이 사라졌다.
 */
const SyntaxHint = ({ item, englishSentence, chunkIndex, fallback = null }) => {
  if (!item?.tokens?.length) return fallback;

  const range =
    chunkIndex === null || chunkIndex === undefined
      ? { from: item.tokens[0].index, to: item.tokens[item.tokens.length - 1].index }
      : findChunkTokenRange(item.tokens, englishSentence, chunkIndex);
  if (!range) return fallback;

  // 낱말마다 색 하나. **좁은 것이 이긴다.** 넓은 절이 통째로 목적어라도 그 안의
  // 주어와 동사는 각자의 색으로 보여야 구조가 읽힌다.
  const colorByToken = new Map();
  const widthByToken = new Map();
  const roleMarkMap = new Map();
  // 열리고 닫히는 자리. 한 자리에 여럿이 겹칠 수 있어 배열로 모은다.
  const openMap = new Map();
  const closeMap = new Map();

  (item.parsed_syntax || []).forEach((one) => {
    if (SKIPPED_TYPE_SET.has(one.type)) return;

    const indexList = one.token_indices || [];
    const inside = indexList.filter(
      (index) => index >= range.from && index <= range.to
    );
    if (!inside.length) return;

    const role = ROLE_STYLE[one.type];
    if (role) {
      const width = indexList.length;
      inside.forEach((index) => {
        if (!widthByToken.has(index) || widthByToken.get(index) > width) {
          widthByToken.set(index, width);
          colorByToken.set(index, role.color);
        }
      });
      // S, V, O 는 그 성분이 **시작하는 낱말 아래**에만 적는다. 낱말마다 적으면
      // 같은 글자가 줄줄이 늘어서 무엇이 한 덩어리인지 되레 흐려진다.
      const startIndex = Math.min(...inside);
      const before = roleMarkMap.get(startIndex);
      if (!before || before.width > width) {
        roleMarkMap.set(startIndex, { label: role.label, color: role.color, width });
      }
      return;
    }

    const phrase = phraseInfo[one.type];
    if (!phrase) return;

    const color = one.type.startsWith("cls:") ? CLAUSE_COLOR : PHRASE_COLOR;
    const [open, close] = bracketOf(one.type);
    const spanFrom = Math.min(...indexList);
    const spanTo = Math.max(...indexList);

    // 괄호는 **실제로 열리고 닫히는 자리에만** 그린다. 덩어리 경계에서 잘린 쪽에
    // 그리면 이 덩어리 안에서 절이 끝난 것처럼 보인다.
    if (spanFrom >= range.from) {
      const list = openMap.get(spanFrom) || [];
      list.push({ name: phrase.tagText, bracket: open, color, width: indexList.length });
      openMap.set(spanFrom, list);
    }
    if (spanTo <= range.to) {
      const list = closeMap.get(spanTo) || [];
      list.push({ bracket: close, color, width: indexList.length });
      closeMap.set(spanTo, list);
    }
  });

  if (!colorByToken.size && !openMap.size) return fallback;

  const tokenByIndex = new Map(item.tokens.map((token) => [token.index, token]));

  /** 낱말 한 칸. 위에 구·절 이름, 가운데 글자, 아래 S·V·O. 빈 칸도 자리를 잡는다. */
  const column = (key, text, color, topLabel, bottomLabel) => (
    <span key={key} className="direct-translation__syntax-column">
      <span className="direct-translation__syntax-name" style={{ color }}>
        {topLabel || ""}
      </span>
      <span
        className="direct-translation__syntax-word"
        style={color ? { color } : undefined}
      >
        {text}
      </span>
      <span className="direct-translation__syntax-role" style={{ color }}>
        {bottomLabel || ""}
      </span>
    </span>
  );

  const piece = [];
  for (let index = range.from; index <= range.to; index++) {
    const token = tokenByIndex.get(index);
    if (!token) continue;

    // 넓은 것이 바깥이다. 열 때는 넓은 것부터, 닫을 때는 좁은 것부터.
    const openList = (openMap.get(index) || []).sort((a, b) => b.width - a.width);
    const closeList = (closeMap.get(index) || []).sort((a, b) => a.width - b.width);
    const mark = roleMarkMap.get(index);

    // **앞 낱말에 붙여 쓸지는 데이터가 정한다.** 구문분석은 `can't` 를 `ca` + `n't`
    // 로 쪼개 놓는데, 토큰마다 무조건 띄우면 `ca n't` 로 벌어진다. 앞 토큰의
    // `space_right` 가 0 이면 붙여 쓰라는 뜻이다.
    const previous = tokenByIndex.get(index - 1);
    const isTight = previous && !previous.space_right;

    // **괄호와 낱말을 한 덩어리로 묶는다.** 따로 두면 줄이 바뀔 때 닫는 괄호만
    // 혼자 다음 줄로 넘어가 엉뚱한 자리에 떨어진다.
    piece.push(
      <span
        key={`g${index}`}
        className={`direct-translation__syntax-group${isTight ? " is-tight" : ""}`}
      >
        {openList.map((one, order) =>
          column(`o${index}-${order}`, one.bracket, one.color, one.name, "")
        )}
        {column(
          `w${index}`,
          token.text,
          colorByToken.get(index),
          "",
          mark ? mark.label : ""
        )}
        {closeList.map((one, order) =>
          column(`c${index}-${order}`, one.bracket, one.color, "", "")
        )}
      </span>
    );
  }

  return <span className="direct-translation__syntax">{piece}</span>;
};

export default SyntaxHint;
